<template>
  <BButton
    ref="bButton"
    v-bind="$attrs"
    :variant="proxyVariant"
    :size="toBvnSize(size)"
    :disabled="status === 'disabled'"
    :data-status="status"
    :class="[
      'ui-button',
      `ui-button--${status}`,
      variant ?
        `ui-button--${variant}` : '',
      size ?
        `ui-button--${size}` : '',
      {
        'ui-button--waiting': status === 'deffered',
        'ui-button--outline': outline,
      }
    ]"
    v-on="{
      click: onClick_
    }"
  >
    <BSpinner
      v-if="status === 'deffered'"
      class="ui-button__spinner"
      small
    />
    <span class="ui-button__inner">
      <slot />
    </span>
  </BButton>
</template>

<style lang="sass">
$spinner-size: 9px

.ui-button
  $self: &

  position: relative

  // /!\ "translate" CSS modifiers won't work with "bootstrap" spinner
  &__spinner
    position: absolute
    left: calc(50% - #{ $spinner-size })
    top: calc(50% - #{ $spinner-size })

  &--waiting

    #{ $self }__inner
      visibility: hidden

  /// With inner <ui-icon>
  &__inner .ui-icon,
  &__inner .common-icon
    $best-matching-size: 120%
    $offset-y: -1px
    font-size: $best-matching-size
    vertical-align: middle
    line-height: 0 // avoid container resizing
    position: relative
    top: $offset-y

  /// Status: accentuate disabled color
  &--disabled#{&}--light &__inner
    color: $gray-medium

  /// Statuses
  &--idled
    @include no-interaction

</style>

<script lang="ts">
import type { BButton } from '#components'
import type { ButtonVariant } from 'bootstrap-vue-next'
import { defineComponent, type SlotsType } from 'vue'
import { useLegacyRootNode } from '~/composables/core/dom'
import { toBvnSize, type SIZE, type STATUS, type VARIANT } from '~/utils/lib/layout/ui'
import { includes, Maybe, not } from '~/utils/lib/core/prelude'

export const ButtonProps = {
  variant: {
    type: String as PropType<VARIANT>,
    required: false,
    default: 'light' satisfies VARIANT
  },
  size: {
    type: String as PropType<SIZE>,
    required: false,
    default: 'md' satisfies SIZE
  },
  status: {
    type: String as PropType<STATUS>,
    required: false,
    default: 'enabled' satisfies STATUS
  },
  outline: {
    type: Boolean,
    required: false,
    default: false
  },
} as const
  // @link https://github.com/vuejs/composition-api/issues/106

export default defineComponent({
  props: {
    ...ButtonProps,
  },
  /// @XXX "vue3" Options API and deprecated $listeners on $attrs horrendous
  ///      design
  ///    ↳ consequence: Options API put its event to shambles troughout the
  ///      the $attrs
  ///         a) thus passing an event "@myEvent" to the component results
  ///            in a `$attrs.onMyEvent`
  ///         b) so using a `v-bind="...$attrs"` on a child component will
  ///            transmit this `onMyEvent` prop
  ///         c) BUT if a `@myEvent` is also added in this child component,
  ///            it will **double** the execution: one from the `$attrs.
  ///            myEvent` setup on the component itself ; another one transmit
  ///            to child component (no resolution made)
  ///    ↳ workaround: as the discussion states → the API bears is smelly,
  ///      and is a major break from v2 to v3 → as comments state, solution
  ///      are empirical depending on the context... [1]
  ///    ↳ empirical surgery: opting for the emit event override [2]
  ///
  /// @link https://github.com/vuejs/rfcs/discussions/397 [1]
  /// @link https://github.com/vuejs/rfcs/discussions/397#discussioncomment-10893510 [2]
  emits: [ 'click' ] as const,
  slots: Object as SlotsType<{
    'default'?: {},
  }>,
  setup() {
    const ref_bButton
      = useTemplateRef<InstanceType<typeof BButton>>('BButton')

    const getRootNode = useLegacyRootNode()

    return {
      ref_bButton,
      getRootNode,
      toBvnSize,
    }
  },
  computed: {
    proxyVariant() {
      const value = this.outline ?
        `outline-${this.variant}` :
        this.variant

      return value as ButtonVariant
    },
    hasPreventClickStatusType() {
      return includes(
        this.status,
        [
          'disabled',
          'idled',
          'deffered',
          'muted'
        ] satisfies STATUS[]
      )
    },
  },
  methods: {
    onClick_(event: Event) {
      if (not(this.hasPreventClickStatusType))
        return this.$emit('click', event)
    },
    focus() {
      Maybe.toMaybe( this.ref_bButton )
        .chain( this.getRootNode<HTMLButtonElement> )
        .map(element => element.focus() )
    },
    getElement() {
      return Maybe.toMaybe( this.ref_bButton )
        .chain( this.getRootNode<HTMLButtonElement> )
    },
  }
})
</script>
